import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Paperclip,
  Mic,
  Send,
  SquarePen,
  Trash2,
  Move,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  Download,
} from "lucide-react";
import TextInput from "../components/ui/TextInput";
import UploadFileCard from "../components/ui/UploadFileCard";
import toast from "../lib/utils/toast";
import {
  uploadPptKnowledgeBase,
  generatePresentationOutline,
  generateFinalPresentation,
  checkPPTGenerationStatus,
  getContentHistory,
} from "../lib/redux/actions";
import ThemeSelectionModal from "../components/presentation/ThemeSelectionModal";
import SiderModal from "../components/presentation/SiderModal";
import daburLogo from "../assets/svg/appIcon.svg";
import Avatar from "../assets/svg/avatar.svg";
import classNames from "classnames";
import InfiniteScroll from "react-infinite-scroll-component";
import { useOutletContext } from "react-router-dom";
import { Box, Modal, Menu, MenuItem, IconButton } from "@mui/material";
import FileCard from "../components/chat/FileCard";
import { downloadFile } from "../lib/utils/utils";
import PPTSkeletonLoader from "../components/presentation/PPTSkeletonLoader";
import WaveTextLoader from "../lib/common/components/loader/WaveTextLoader";
import Tooltip from "../components/tooltip/tooltip";
import ToolTipQuestionMark from "../components/presentation/ToolTipQuestionMark";
import { MESSAGES } from "../config/messages";
import UploadingLoader from "../lib/common/components/loader/UploadingLoader";


const getSlideSrc = (base64) => {
  if (!base64) return "";
  if (base64.startsWith("data:image")) return base64;
  return `data:image/jpeg;base64,${base64}`;
};

// Task status constants
const TASK_STATUS = {
  PENDING: 1,
  PROCESSING: 2,
  SUCCESS: 3,
  FAILED: 4,
};

export default function GeneratePresentation() {
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const presentationRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [view, setView] = useState("compose"); // "compose" | "outline" | "edit"
  const [presentation, setPresentation] = useState(null);
  const [activeSlideId, setActiveSlideId] = useState(null);
  const [themeIndex, setThemeIndex] = useState(0);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState("t1");
  const [isEditingSlide, setIsEditingSlide] = useState(false);
  const [editDraft, setEditDraft] = useState({ title: "", description: "" });
  const [draggedSlideIndex, setDraggedSlideIndex] = useState(null);

  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [chats, setChats] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isGeneratingFinal, setIsGeneratingFinal] = useState(false);
  const [outlineApiData, setOutlineApiData] = useState(null); // Store presentationId, chatId, and original outline from API
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(true);
  const [includeToc, setIncludeToc] = useState(false);
  const [includeCover, setIncludeCover] = useState(false);
  const [includeImages, setIncludeImages] = useState(false);
  const [style, setStyle] = useState("Default");
  const [tone, setTone] = useState("default");     // backend value
  const [detailLevel, setDetailLevel] = useState("Basic");
  // const [aiImageMode, setAiImageMode] = useState("AI Image");
  const [slideOption, setSlideOption] = useState("1");
  const [openMenu, setOpenMenu] = useState(null); // "style" | "slides" | "detail" | "ai" | null
  const controlsRef = useRef(null);

  const hasStartedInitialTimer = useRef(false);
  const [initialUploadInProgress, setInitialUploadInProgress] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const { isMobile } = useOutletContext();

  const [pptPreview, setPptPreview] = useState({
    open: false,
    slides: [],
    activeIndex: 0,
    fileUrl: null,
  });

  const TONE_OPTIONS = {
    "Default": "default",
    "Casual": "casual",
    "Funny": "funny",
    "Professional":
      "professional",
    "Educational": "educational",
    "Sales Pitch": "sales_pitch"
  }
    ;

  // [basic, detailed, comprehensive]",
  const DETAIL_OPTIONS = ["Basic", "Detailed", "Comprehensive"];
  const SLIDE_LABEL_OPTIONS = Array.from({ length: 50 }, (_, i) =>
    String(i + 1)
  );

  const slideCountLabel = useMemo(() => {
    const text = slideOption || "";
    // Handle simple number format (e.g., "1", "2", "3", etc.)
    if (text && text !== "No of Slides") {
      const num = parseInt(text, 10);
      if (!isNaN(num) && num > 0) {
        return `${num} slides`;
      }
    }
    // Fallback to default
    return "5 slides";
  }, [slideOption]);

  // Previously used to create inline SVG thumbnails; now real thumbnails come from API.

  useEffect(() => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const result = event.results[event.results?.length - 1][0].transcript;
      setPrompt((prev) => `${prev}${result}`);
    };
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch (e) {
        // ignore
      }
      recognitionRef.current = null;
    };
  }, []);

  // useEffect(() => {
  //   const onDocMouseDown = (e) => {
  //     if (!controlsRef.current) return;
  //     if (!controlsRef.current.contains(e.target)) {
  //       setOpenMenu(null);
  //     }
  //   };
  //   document.addEventListener("mousedown", onDocMouseDown);
  //   return () => document.removeEventListener("mousedown", onDocMouseDown);
  // }, []);

  // Debug: Log state changes to help identify issues
  useEffect(() => {
    console.log("State updated:", {
      style,
      detailLevel,
      slideOption,
      slideCountLabel,
      includeCover,
      includeToc,
      includeImages,
    });
  }, [
    style,
    detailLevel,
    slideOption,
    slideCountLabel,
    includeCover,
    includeToc,
    includeImages,
  ]);

  // Sync editable draft with the currently active slide when entering Edit view
  // (but don't overwrite while the user is actively editing)
  useEffect(() => {
    if (view !== "edit") return;
    if (!presentation) return;
    if (isEditingSlide) return;

    const slide =
      presentation?.slides?.find((s) => s?.id === activeSlideId) ||
      presentation?.slides?.[0];
    if (!slide) return;

    setEditDraft({
      title: slide?.title || "",
      description: slide?.description || "",
    });
  }, [view, presentation, activeSlideId, isEditingSlide]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Load chat history on mount
  useEffect(() => {
    handleGetContentHistory(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFileNameFromUrl = (url = "") => {
    try {
      return new URL(url).pathname.split("/").pop();
    } catch {
      return "presentation.pptx";
    }
  };

  const handleGetContentHistory = async (
    pageNumber = 1,
    isChatLoading = false
  ) => {
    if (isFetchingRef.current) return;

    try {
      if (isChatLoading) setIsChatLoading(true);
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);

      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 10,
        chatType: 6, // PRESENTATION type
      };

      const res = await getContentHistory(data);
      const newChats = res.data.data || [];

      // Filter out duplicate entries (where questionId is null)
      const filteredChats = newChats.filter((chat) => chat.questionId !== null);

      const existingIds = new Set(chats.map((chat) => chat?._id));
      const uniqueNewChats = filteredChats.filter(
        (chat) => !existingIds.has(chat?._id)
      );

      // Transform API response to chat format
      const processedMessages = uniqueNewChats.flatMap((chat) => {
        const userMessage = {
          role: "user",
          message: chat?.questionId?.message,
          _id: chat?.questionId?._id,
          type: "text",
          ...(chat?.questionId?.media &&
            chat?.questionId?.media?.length > 0 && {
            media: chat?.questionId?.media || [],
          }),
        };

        // Check if assistant message has PPT output
        // Backend will send slides data similar to files constant structure (fileUrl, fileName)
        // For now using files as placeholder, backend will replace this
        const assistantMessage = {
          role: "assistant",
          type: chat?.outputUrl ? "ppt" : "text",
          message: chat?.message || "Presentation generated",
          _id: chat?._id,
          ...(chat?.outputUrl && {
            fileUrl: chat?.outputUrl,
            fileName: getFileNameFromUrl(chat?.outputUrl),
            // Backend will send slides array with structure like files constant: [{ fileUrl, fileName }, ...]
            slides: chat?.slide_images || [],
          }),
        };

        return [userMessage, assistantMessage];
      });

     
      setChats((prev) => [...processedMessages, ...prev]);
      setHasMore(processedMessages.length > 0);

      // Set view based on whether there are chats (only on first page load)
      if (pageNumber === 1) {
        // Check if we have any chats (processed messages or existing chats)
        const hasChats = processedMessages.length > 0 || chats.length > 0;
        if (hasChats) {
          setView("chat");
        } else {
          setView("compose");
        }
      }

      // After state update, adjust scroll position
      // setTimeout(() => {
      //   if (scrollRef.current) {
      //     const newScrollHeight = scrollRef.current.scrollHeight;
      //     const prevScrollTop = scrollRef.current.scrollTop;
      //     const scrollDiff = newScrollHeight - scrollHeightRef.current;
      //     scrollRef.current.scrollTop = prevScrollTop + scrollDiff;
      //   }
      // }, 0);
    } catch (error) {
      console.error("Error fetching chat history:", error);
      // On error, show compose view
      if (pageNumber === 1) {
        setView("compose");
      }
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (isChatLoading) setIsChatLoading(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
    }
  };

  const fetchMoreData = async () => {
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsLoadingMore(true);

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      setPage(nextPage);
      await handleGetContentHistory(nextPage);
    }, 300); // 300ms debounce
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error("Speech recognition not supported in this browser.");
      return;
    }
    if (loading || isUploading) return;

    try {
      if (isRecording) {
        recognitionRef.current.stop();
        setIsRecording(false);
      } else {
        recognitionRef.current.start();
        setIsRecording(true);
      }
    } catch (e) {
      setIsRecording(false);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e?.target?.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
      "application/vnd.ms-powerpoint", // .ppt
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "text/plain", // .txt
      "text/markdown", // .md
    ];

    const allowedExtensions = [".pptx", ".ppt", ".docx", ".doc", ".xlsx", ".pdf", ".txt", ".md"];

    const MAX_FILE_SIZE_MB = 10;
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        toast.error(`Unsupported format: ${file.name}. Supported: .pptx, .ppt, .docx, .doc, .xlsx, .pdf, .txt, .md`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`File size is too big (Maximum allowed size is 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    try {
      setIsUploading(true);

      // Upload files one by one using uploadPptKnowledgeBase
      const uploadedFiles = [];
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append("file", file); // Note: API expects "file" not "files"

        // const result = await uploadPptKnowledgeBase(formData);
        const result = await uploadPptKnowledgeBase(formData);

        // const result = {
        //     "statusCode": 200,
        //     "message": "Document uploaded successfully to Slidespeak",
        //     "data": {
        //         "task_id": "6d47b758-ece4-496d-8fe3-f367d3cb0393",
        //         "blog_url": null,
        //         "file_url": "https://daburindia.blob.core.windows.net/daburindia/ 1766750324749-1-4740551620095_795798549_87551XXXXX_12_2025.pdf",
        //         "uploaded_at": "2025-12-26T11:58:51.066Z"
        //     },
        //     "status": 1
        // }

        if (result?.statusCode === 200 && result?.data?.file_url) {
          uploadedFiles.push({
            fileUrl: result?.data?.file_url,
            fileName: file?.name,
            taskId: result?.data?.task_id, // Store task_id for later use in generate API
          });
        } else {
          // Handle error for this specific file
          const errorMessage =
            result?.error || result?.message || "File upload failed";
          toast.error(`${file.name}: ${errorMessage}`);
        }
      }

      if (uploadedFiles.length > 0) {
        setFiles((prev) => [...prev, ...uploadedFiles]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("File upload error:", err);
      toast.error(err?.message || "File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return next;
    });
  };

  const handleGenerate = async ({ text, media = [] } = {}) => {
    const userPrompt = text ?? prompt;
    if (!userPrompt?.trim()) {
      toast.error("Please enter a prompt.");
      return;
    }

    if (userPrompt.trim().length < 5) {
      toast.error("Prompt must be at least 5 characters long.");
      return;
    }

    if (style?.trim() === "Select Tone" || style?.trim() === "") {
      toast.error("Please select a tone.");
      return;
    }
    if (
      detailLevel?.trim() === "Select Detail Level" ||
      detailLevel?.trim() === ""
    ) {
      toast.error("Please select a detail level.");
      return;
    }
    if (!slideOption?.trim()) {
      toast.error("Please select a number of slides.");
      return;
    }

    // if(!files.length > 0) {
    //   toast.error("Please upload at least one document.");
    //   return;
    // }
    if (loading || isUploading) {
      toast.error("Please wait file is uploading...");
      return;
    }
    if (view !== "chat") {
      setView("chat");
    }
    setLoading(true);

    // Clear previous outline API data when starting a new generation
    setOutlineApiData(null);

    const userMessage = userPrompt;
    const loadingMessageId = `loading-${Date.now()}`;

    // EXACT same flow as GenerateImage: append user first, then assistant loading.
    // With the chat container using flex-col-reverse + scrollTop=0, newest stays near the input.
    setChats((prev) => [
      ...prev,
      {
        role: "user",
        type: "text",
        message: userMessage,
        ...(media.length > 0 && { media: media.map((file) => file.fileUrl) }),
      },
      {
        role: "assistant",
        type: "loading",
        message: "Generating presentation outline...",
        _id: loadingMessageId,
      },
    ]);

    try {
      // Get current state values to avoid closure issues
      const currentStyle = tone;
      const currentDetailLevel = detailLevel;
      const currentSlideOption = slideOption;
      const currentIncludeCover = includeCover;
      const currentIncludeToc = includeToc;
      const currentIncludeImages = includeImages;
      const currentFiles = files;

      // Clear form states after capturing values
      setPrompt("");
      setFiles([]);
      // Keep dropdown values (style, detailLevel, slideOption) so user can reuse settings
      setIncludeCover(false);
      setIncludeToc(false);
      setIncludeImages(false);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Parse slide count directly from slideOption (now simple numbers like "1", "2", "3", etc.)
      // Handle cases where slideOption might be "No of Slides" or invalid
      let slideCount = 5; // default
      if (currentSlideOption && currentSlideOption !== "No of Slides") {
        const parsed = parseInt(currentSlideOption, 10);
        if (!isNaN(parsed) && parsed > 0) {
          slideCount = parsed;
        }
      }

      // Prepare images array from media/files
      // Combine media parameter and currentFiles, then deduplicate by URL
      const allFileUrls = [];
      if (media.length > 0) {
        allFileUrls.push(...media.map((f) => f?.fileUrl || f));
      }
      if (currentFiles.length > 0) {
        allFileUrls.push(...currentFiles.map((f) => f?.fileUrl || f));
      }
      // Remove duplicates by converting to Set and back to array
      const images = [...new Set(allFileUrls)];

      // Extract taskIds from files (both from media parameter and currentFiles state)
      const taskIds = [];
      if (media.length > 0) {
        media.forEach((f) => {
          if (f?.taskId) taskIds.push(f?.taskId);
        });
      }
      if (currentFiles.length > 0) {
        currentFiles.forEach((f) => {
          if (f?.taskId) taskIds.push(f?.taskId);
        });
      }
      // Remove duplicate taskIds
      const uniqueTaskIds = [...new Set(taskIds)];

      const payload = {
        prompt: userPrompt,
        ...(images.length > 0 && { images }),
        ...(uniqueTaskIds.length > 0 && { taskIds: uniqueTaskIds }),
        ...(currentStyle !== "Select Tone" && {
          tone: currentStyle.toLowerCase(),
        }),
        ...(currentDetailLevel !== "Select Detail Level" && {
          detailLevel: currentDetailLevel.toLowerCase(),
        }),
        slides: slideCount,
        includeCover: currentIncludeCover,
        includeTableOfContents: currentIncludeToc,
        includeImages: currentIncludeImages,
      };

      const response = await generatePresentationOutline(payload);

      if (response?.statusCode === 200 || response?.data) {
        // setChats((prev) => prev.slice(0, -2));
        setChats((prev) => prev.filter((c) => c?._id !== loadingMessageId));

        const outlineData = response.data || response;

        // Store API response data for use in generateFinalPresentation
        setOutlineApiData({
          presentationId: outlineData?.presentationId,
          chatId: outlineData?.chatId,
          media:media?.length > 0 ? media : [],
          originalOutline: outlineData?.outline, // Store the original outline string/object
        });

        // Parse the outline JSON string if it exists
        let parsedOutline = null;
        if (outlineData?.outline) {
          try {
            parsedOutline =
              typeof outlineData?.outline === "string"
                ? JSON.parse(outlineData?.outline)
                : outlineData?.outline;
          } catch (parseError) {
            console.error("Error parsing outline JSON:", parseError);
            parsedOutline = null;
          }
        }

        // Transform outline object to slides array
        let slides = [];
        if (parsedOutline && typeof parsedOutline === "object") {
          // Convert outline object (slide_1, slide_2, etc.) to array
          const slideKeys = Object.keys(parsedOutline).sort((a, b) => {
            const numA = parseInt(a.replace("slide_", ""), 10);
            const numB = parseInt(b.replace("slide_", ""), 10);
            return numA - numB;
          });

          slides = slideKeys.map((key, index) => {
            const slideData = parsedOutline?.[key];
            const slideNumber = index + 1;

            return {
              id: `s${slideNumber}`,
              label: `Slide ${slideNumber}`,
              title:
                slideData?.topic || slideData?.title || `Slide ${slideNumber}`,
              description: Array.isArray(slideData?.content)
                ? slideData?.content?.join("\n")
                : slideData?.content || slideData?.description || "",
            };
          });
        } else if (Array.isArray(outlineData?.slides)) {
          // Fallback: if slides is already an array, use it directly
          slides = outlineData?.slides;
        } else if (Array.isArray(outlineData?.outline)) {
          // Fallback: if outline is already an array
          slides = outlineData?.outline;
        }

        // Transform API response to match presentation structure
        const transformedPresentation = {
          id:
            outlineData?.presentationId || outlineData?.id || `p-${Date.now()}`,
          prompt: userPrompt,
          meta: {
            tone: style,
            slides: slideCountLabel,
            detailLevel,

            includeCover,
            includeToc,
            includeImages,
          },
          slides: slides,
        };

        setPresentation(transformedPresentation);
        presentationRef.current = transformedPresentation; // Update ref
        setActiveSlideId(transformedPresentation.slides?.[0]?.id || null);

        // Remove loading message from chats
        setChats((prev) =>
          prev.filter((chat) => chat?._id !== loadingMessageId)
        );

        setView("outline");
        setOpenMenu(null);
        // States already cleared after capturing values above
      } else {
        throw new Error(response?.message || "Failed to generate outline");
      }
    } catch (error) {
      console.error("Error generating outline:", error);
      toast.error(
        error?.message ||
        "Failed to generate presentation outline. Please try again."
      );

      // Replace loading message with error message
      setChats((prev) =>
        prev.map((chat) =>
          chat?._id === loadingMessageId
            ? {
              role: "assistant",
              type: "error",
              message:
                error?.message ||
                "Failed to generate presentation outline. Please try again.",
              _id: loadingMessageId,
            }
            : chat
        )
      );

      // Fallback to dummy data for development
      // const next = createDummyPresentation();
      // setPresentation(next);
      // presentationRef.current = next; // Update ref
      // setActiveSlideId(next.slides?.[0]?.id || null);
      // setView("outline");
      setOpenMenu(null);
      // States already cleared after capturing values above
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSlide = (slideId) => {
    // Row click only expands/collapses in outline/edit list (does NOT enter edit mode)
    setActiveSlideId((prev) => (prev === slideId ? null : slideId));
  };

  const handleEditSlide = (slideId) => {
    setActiveSlideId(slideId);
    setView("edit");
    const slide = presentation?.slides?.find((s) => s?.id === slideId);
    if (slide) {
      setEditDraft({
        title: slide?.title || "",
        description: slide?.description || "",
      });
    }
    setIsEditingSlide(true);
  };

  const handleDeleteSlide = (slideId) => {
    setPresentation((prev) => {
      if (!prev) return prev;
      const nextSlides = prev?.slides?.filter((s) => s?.id !== slideId) || [];
      const updated = { ...prev, slides: nextSlides };
      presentationRef.current = updated; // Update ref
      return updated;
    });
    if (activeSlideId === slideId) setActiveSlideId(null);
  };

  const handleUpdateSlide = (slideId, patch) => {
    setPresentation((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        slides:
          prev?.slides?.map((s) =>
            s?.id === slideId ? { ...s, ...patch } : s
          ) || [],
      };
      // Update ref to track latest state
      presentationRef.current = updated;

      return updated;
    });
  };

  // When user clicks the bottom "Generate Presentation" button,
  // send outline data + template to API and show response in chat list
  const handleGenerateFinalPresentation = async () => {
    if (!presentation) {
      toast.error("Please generate an outline first.");
      return;
    }

     if (initialUploadInProgress) {
      setShowDisclaimer(true);
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }


    hasStartedInitialTimer.current = false;
    setShowDisclaimer(false);
    // const userMessage =
    //   presentation?.prompt?.trim() ||
    //   "Generate a final presentation PDF based on the current outline.";

    // EXACT same flow as GenerateImage: append user first, then assistant loading.
    // With the chat container using flex-col-reverse + scrollTop=0, newest stays near the input.
    setChats((prev) => [
      ...prev,
      {
        role: "assistant",
        type: "loading",
        message: "Generating presentation...",
      },
    ]);

    // Switch to chat-style view
    setView("chat");

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);

    setIsGeneratingFinal(true);

    try {
      // Check if we have the API data from generatePresentationOutline
      if (
        !outlineApiData ||
        !outlineApiData?.presentationId ||
        !outlineApiData?.chatId
      ) {
        throw new Error(
          "Outline API data not available. Please generate an outline first."
        );
      }

      // Use ref to get the absolute latest state (includes any pending updates)
      // Fallback to state if ref is not set
      const currentPresentation = presentationRef.current || presentation;

      if (!currentPresentation || !currentPresentation?.slides) {
        throw new Error("Presentation data not available");
      }

      // Get the current slides - use ref value to ensure we have latest edits
      const currentSlides = currentPresentation?.slides || [];

      // Log current slides before mapping to debug

      // Transform slides to the required format: object with slide_1, slide_2, etc.
      // Format: { "slide_1": { "topic": "...", "content": [...] }, ... }
      const transformedOutline = {};
      currentSlides.forEach((slide, index) => {
        const slideKey = `slide_${index + 1}`;

        // Extract content from description (split by newlines and filter empty strings)
        let content = [];
        if (slide?.description) {
          const lines = slide?.description
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

          // If we have multiple lines, use them as content
          // If we have a single line, use it as a single content item
          content =
            lines.length > 0
              ? lines
              : [slide?.description?.trim()].filter((c) => c);
        }

        transformedOutline[slideKey] = {
          topic: slide?.title || "",
          content: content,
        };
      });

      const payload = {
        chatId: outlineApiData?.chatId,
        presentationId: outlineApiData?.presentationId,
        outline: JSON.stringify(transformedOutline), // Convert object to JSON string
        slides:currentSlides?.length || 0,
      };

      // Log the complete payload for debugging

      const response = await generateFinalPresentation(payload);

      if (response?.statusCode === 200 || response?.data) {
        const presentationData = response?.data || response;
        const presentationId = presentationData?.presentationId;
        const taskStatus = TASK_STATUS.PENDING;

        // Update loading message with initial status
        setChats((prev) => {
          const updated = [...prev];
          const lastIndex = updated
            .map((c) => c?.role + c?.type)
            .lastIndexOf("assistantloading");
          if (lastIndex !== -1) {
            updated[lastIndex] = {
              role: "assistant",
              type: "loading",
              message: "Generating Presentation...",
              presentationId: presentationId,
              status: taskStatus,
            };
          }
          return updated;
        });

        // If status is PENDING or PROCESSING, start polling
        if (
          taskStatus === TASK_STATUS.PENDING ||
          taskStatus === TASK_STATUS.PROCESSING
        ) {
          // Clear any existing polling interval
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }

          pollIntervalRef.current = setInterval(async () => {
            try {
              const statusResponse = await checkPPTGenerationStatus({
                presentationId,
              });
              const statusData = statusResponse?.data || statusResponse;
              // Ensure status is a number for comparison
              const taskStatus = Number(statusData?.status);

              // Debug logging
              if (taskStatus === TASK_STATUS.FAILED) {
                console.error("Presentation generation failed:", {
                  taskStatus,
                  statusData,
                });
              }

              // Handle status based on numeric task status codes
              if (taskStatus === TASK_STATUS.SUCCESS) {
                // Presentation is ready (SUCCESS: 3)
                // const slides =
                //   statusData?.thumbnails?.map((url, idx) => ({
                //     id: `${statusData?.presentationId || "ppt"}_slide_${idx + 1}`,
                //     title: statusData?.title || "Presentation",
                //     thumbUrl: url,
                //     slides:statusData?.slide_images || [],
                //   })) || [];

                setChats((prev) => {
                  const updated = [...prev];
                  const lastIndex = updated
                    .map((c) => c?.role + c?.type)
                    .lastIndexOf("assistantloading");
                  if (lastIndex !== -1) {
                    updated[lastIndex] = {
                      role: "assistant",
                      type: "ppt",
                      message: "Generating Presentation...",
                      fileName: getFileNameFromUrl(statusData?.pptUrl),
                      fileUrl: statusData?.pptUrl,
                      slides: statusData?.slide_images || [],
                      _id: statusData?.presentationId,
                    };
                  }
                  return updated;
                });
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGeneratingFinal(false);
              } else if (
                taskStatus === TASK_STATUS.FAILED ||
                isNaN(taskStatus)
              ) {
                // Task failed (FAILED: 4) or invalid status
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGeneratingFinal(false);

                const errorMessage =
                  statusData?.message ||
                  "Failed to generate presentation. Please try again.";

                setChats((prev) => {
                  const updated = [...prev];
                  const lastIndex = updated
                    .map((c) => c?.role + c?.type)
                    .lastIndexOf("assistantloading");
                  if (lastIndex !== -1) {
                    updated[lastIndex] = {
                      role: "assistant",
                      type: "error",
                      message:
                        statusData?.message ||
                        "Failed to generate presentation. Please try again.",
                    };
                  }
                  return updated;
                });

                toast.error(errorMessage);
              } else if (
                taskStatus === TASK_STATUS.PENDING ||
                taskStatus === TASK_STATUS.PROCESSING
              ) {
                // Still processing (PENDING: 1 or PROCESSING: 2)
                setChats((prev) => {
                  const updated = [...prev];
                  const lastIndex = updated
                    .map((c) => c?.role + c?.type)
                    .lastIndexOf("assistantloading");
                  if (lastIndex !== -1) {
                    updated[lastIndex] = {
                      role: "assistant",
                      type: "loading",
                      message: "Generating Presentation...",
                      presentationId: presentationId,
                      status: taskStatus,
                    };
                  }
                  return updated;
                });
              } else {
                // Unknown status - treat as error
                console.warn(
                  "Unknown task status received:",
                  taskStatus,
                  statusData
                );
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGeneratingFinal(false);

                const errorMessage =
                  statusData?.message ||
                  "Unknown status received. Please try again.";

                setChats((prev) => {
                  const updated = [...prev];
                  const lastIndex = updated
                    .map((c) => c?.role + c?.type)
                    .lastIndexOf("assistantloading");
                  if (lastIndex !== -1) {
                    updated[lastIndex] = {
                      role: "assistant",
                      type: "error",
                      message: errorMessage,
                    };
                  }
                  return updated;
                });

                toast.error(errorMessage);
              }
            } catch (error) {
              console.error("Error checking PPT generation status:", error);
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              setIsGeneratingFinal(false);

              setChats((prev) => {
                const updated = [...prev];
                const lastIndex = updated
                  .map((c) => c?.role + c?.type)
                  .lastIndexOf("assistantloading");
                if (lastIndex !== -1) {
                  updated[lastIndex] = {
                    role: "assistant",
                    type: "error",
                    message:
                      error?.message ||
                      "Failed to generate final presentation. Please try again.",
                  };
                }
                return updated;
              });
            }
          }, 15000); // Poll every 15 seconds (same as DeepSearchChatInterface)
        } else if (taskStatus === TASK_STATUS.SUCCESS) {
          // Already complete (SUCCESS: 3) - shouldn't happen often, but handle it
          // const slides =
          //   presentationData?.thumbnails?.map((url, idx) => ({
          //     id: `${presentationData?.presentationId || "ppt"}_slide_${idx + 1}`,
          //     title: presentationData?.title || "Presentation",
          //     thumbUrl: url,
          //   })) || [];

          setChats((prev) => {
            const updated = [...prev];
            const lastIndex = updated
              .map((c) => c?.role + c?.type)
              .lastIndexOf("assistantloading");
            if (lastIndex !== -1) {
              updated[lastIndex] = {
                role: "assistant",
                type: "ppt",
                message: `Your presentation is ready: ${presentationData?.title || "Presentation"
                  }`,
                fileName: getFileNameFromUrl(presentationData?.pptUrl),
                fileUrl: presentationData?.pptUrl,
                slides: presentationData?.slide_images || [],
                _id: presentationData?.presentationId,
              };
            }
            return updated;
          });
          setIsGeneratingFinal(false);
        } else if (taskStatus === TASK_STATUS.FAILED) {
          // Task failed immediately
          setChats((prev) => {
            const updated = [...prev];
            const lastIndex = updated
              .map((c) => c?.role + c?.type)
              .lastIndexOf("assistantloading");
            if (lastIndex !== -1) {
              updated[lastIndex] = {
                role: "assistant",
                type: "error",
                message:
                  "Failed to generate final presentation. Please try again.",
              };
            }
            return updated;
          });
          setIsGeneratingFinal(false);
        } else {
          setChats((prev) => {
            const updated = [...prev];
            const lastIndex = updated
              .map((c) => c?.role + c?.type)
              .lastIndexOf("assistantloading");
            if (lastIndex !== -1) {
              updated[lastIndex] = {
                role: "assistant",
                type: "error",
                message:
                  "Failed to generate final presentation. Please try again.",
              };
            }
            return updated;
          });
          // throw new Error(presentationData?.message || "Failed to generate final presentation");
        }
      } else {
        // throw new Error(response?.message || "Failed to generate final presentation");
        setChats((prev) => {
          const updated = [...prev];
          const lastIndex = updated
            .map((c) => c?.role + c?.type)
            .lastIndexOf("assistantloading");
          if (lastIndex !== -1) {
            updated[lastIndex] = {
              role: "assistant",
              type: "error",
              message:
                "Failed to generate final presentation. Please try again.",
            };
          }
          return updated;
        });
      }
    } catch (error) {
      console.error("Error generating final presentation:", error);

      setChats((prev) => {
        const updated = [...prev];
        const lastIndex = updated
          .map((c) => c?.role + c?.type)
          .lastIndexOf("assistantloading");
        if (lastIndex !== -1) {
          updated[lastIndex] = {
            role: "assistant",
            type: "error",
            message:
              error?.message ||
              "Failed to generate final presentation. Please try again.",
          };
        }
        return updated;
      });

      toast.error(
        error?.message ||
        "Failed to generate final presentation. Please try again."
      );
    } finally {
      // setIsGeneratingFinal(false);
    }
  };


  const HOLD_TIME_SECONDS = Number(
    process.env.REACT_APP_HOLD_TIME_FOR_GENERATE_PPT
  );

  const HOLD_TIME_MS = HOLD_TIME_SECONDS ? HOLD_TIME_SECONDS : 20000;
  const timerRef = useRef(null);
  // Disable button for first 30 seconds when page loads
  useEffect(() => {
    // let timer;

    if (
      (view === "outline" || view === "edit") &&
      !hasStartedInitialTimer.current &&
      outlineApiData?.media?.length > 0
    ) {
      hasStartedInitialTimer.current = true;
      setInitialUploadInProgress(true);

      timerRef.current = setTimeout(() => {
        setInitialUploadInProgress(false);
      }, HOLD_TIME_MS);
    }

    // return () => {
    //   if (timer) clearTimeout(timer);
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Chat-style handler once we're in the "normal agent" view.
  const handleChatMessage = async ({ text, media = [] } = {}) => {
    const userPrompt = text ?? prompt;
    if (!userPrompt?.trim()) {
      toast.error("Please enter a prompt.");
      return;
    }

    // Generate presentation outline (same as handleGenerate)
    await handleGenerate({ text: userPrompt, media });
  };

  const handleThemeApply = ({ themeId, templateId }) => {
    setThemeIndex(themeId);
    setActiveTemplateId(templateId);
  };

  const handleDragStart = (index) => {
    setDraggedSlideIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = (dropIndex) => {
    if (draggedSlideIndex === null || draggedSlideIndex === dropIndex) return;

    const newSlides = [...(presentation?.slides || [])];
    const [draggedSlide] = newSlides.splice(draggedSlideIndex, 1);
    newSlides.splice(dropIndex, 0, draggedSlide);

    setPresentation((prev) => {
      const updated = { ...prev, slides: newSlides };
      presentationRef.current = updated; // Update ref
      return updated;
    });
    setDraggedSlideIndex(null);
  };

  // Scroll helper for chat view (match GenerateImage behaviour)
  const scrollToBottom = () => {
    if (scrollRef.current) {
      // With flex-col-reverse, scrollTop = 0 shows the latest messages at the bottom
      scrollRef.current.scrollTop = 0;
    }
  };

  if (isChatLoading) {
    return <PPTSkeletonLoader />;
  }

  if (view === "chat") {
    return (
      <div
        className={`flex-1 flex flex-col mt-4 justify-between overflow-scroll mx-4`}
      >
        {isLoadingHistory && chats.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-gray-500">Loading chat history...</span>
          </div>
        ) : (
          <div
            id="scrollableDiv"
            ref={scrollRef}
            className={`flex-1 py sm:px-[50px] md:px-[20px] lg:!px-[100px] 2xl:!px-0  w-full max-w-5xl mx-auto  overflow-y-auto  flex flex-col-reverse mt-[40px] ${!isMobile && " !px-[30px]"
              }`}
          >
            <InfiniteScroll
              inverse={true}
              dataLength={chats.length}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget="scrollableDiv"
              scrollThreshold="200px"
              loader={
                isLoadingMore && !isLoadingHistory ? (
                  <div className="flex justify-center my-4">
                    <span className="text-gray-500">Loading more...</span>
                  </div>
                ) : null
              }
              // endMessage={
              //   !hasMore && chats.length > 0 ? (
              //     <p className="text-center m-2 text-sm text-gray-500">No more chats!</p>
              //   ) : null
              // }
              className="!overflow-hidden"
            >
              {chats?.map((chat, idx) => (
                <div
                  key={chat?._id || chat?.id || `chat-${idx}`}
                  className={`flex mb-4 gap-4 sm:w-full ${chat?.role === "user" ? "justify-end" : "justify-start"
                    }`}
                >
                  {chat?.role !== "user" && !isMobile && (
                    <div className="w-[40px] align-self-center flex-shrink-0">
                      <img
                        src={daburLogo}
                        alt="Dabur Logo"
                        className="w-[40px]"
                      />
                    </div>
                  )}
                  <div
                    className={`text-[#000000] flex flex-col gap-2 overflow-x-auto min-h-[44px] justify-center w-fit group  ${chat?.role === "user"
                      ? " bg-[#1b5c2926] rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] max-w-[85%] sm:max-w-[75%] md:max-w-[70%] w-fit"
                      : ""
                      }`}
                  >
                    {(chat?.type === "text" || chat?.type === "error") && (
                      <span className="p-2">{chat?.message}</span>
                    )}
                    {chat?.type === "loading" && (
                      <span className="italic text-gray-500">
                        <WaveTextLoader text={chat?.message} />
                      </span>
                    )}
                    {chat.type === "ppt" && (
                      <div
                        className="w-[90%] cursor-pointer"
                        onClick={() => {
                          if (chat?.slides?.length > 0) {
                            setPptPreview({
                              open: true,
                              slides: chat.slides || [],
                              activeIndex: 0,
                            });
                          }
                        }}
                      >
                        <div className="flex gap-[10px] overflow-x-auto pb-2">
                          {(chat.slides || []).map((s, slideIdx) => (
                            <div
                              key={slideIdx}
                              className="shrink-0 h-[324px] rounded-[10px] overflow-hidden bg-white border border-[rgba(27,92,41,0.10)] shadow-[0px_1px_4px_rgba(16,24,40,0.06)] flex items-center justify-center"
                              title={`Slide ${slideIdx + 1}`}
                            >
                              {s ? (
                                <img
                                  src={getSlideSrc(s)}
                                  alt={`Slide ${slideIdx + 1}`}
                                  className="w-full h-full object-contain"
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                        {chat?.slides?.length === 0 && (
                          <div className="shrink-0 w-[300px] whitespace-nowrap flex items-center ">
                            <span className="text-gray-500">
                              No slides available
                            </span>
                          </div>
                        )}

                        <div className="mt-2 flex items-center gap-6">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (chat?.fileUrl) {
                                downloadFile(
                                  chat.fileUrl,
                                  "presentation.pptx"
                                );
                              } else {
                                toast.error("Download link not available.");
                              }
                            }}
                          >
                            <span className="w-[34px] h-[34px] rounded-full bg-white border border-[rgba(27,92,41,0.10)] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center">
                              <Download className="w-4 h-4 text-[#1B5C29]" />
                            </span>
                            <span className="text-[13px] font-bold text-[#1B5C29]">
                              Download
                            </span>
                          </button>

                          {/* <button
                          type="button"
                          className="inline-flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="w-[34px] h-[34px] rounded-full bg-white border border-[rgba(27,92,41,0.10)] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center">
                            <Pencil className="w-4 h-4 text-[#1B5C29]" />
                          </span>
                          <span className="text-[13px] font-bold text-[#1B5C29]">
                            Edit
                          </span>
                        </button> */}
                        </div>
                      </div>
                    )}
                    {chat.media && chat.media.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2 p-2 w-fit">
                        {chat.media.map((mediaItem, fileIndex) => (
                          <FileCard
                            index={fileIndex}
                            key={`uploadfilecard-${mediaItem.fileUrl}`}
                            mediaItem={mediaItem}
                            fileIndex={fileIndex}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {chat?.role === "user" && !isMobile && (
                    <div className="!w-[35px] flex-shrink-0">
                      <img
                        src={Avatar}
                        alt="User Avatar"
                        className="w-[40px]"
                      />
                    </div>
                  )}
                </div>
              ))}
            </InfiniteScroll>
          </div>
        )}

        <div
          className={classNames(
            `sticky  bottom-0 mb-0 py-4 sm:!px-[50px] md:!px-[20px] lg:!px-[100px] xl:!px-[100px] 2xl:!px-[100px] ${!isMobile && " !px-[30px]"
            }`,
            {
              "pointer-events-none opacity-60": loading || isGeneratingFinal,
            }
          )}
        >
          <div className="w-full max-w-5xl mx-auto">
            {/* File preview cards - outside the input box */}
             <div className="flex flex-row mb-2 gap-4 items-center">
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {files.map((file, index) => (
                  <UploadFileCard
                    key={`uploadfilecard-${file?.fileUrl}`}
                    file={file}
                    index={index}
                    removeFile={removeFile}
                  />
                ))}
              </div>
            )}
            {isUploading  && <div className="flex flex-row gap-4 items-center ">
                <UploadingLoader />
                <span className="text-sm text-gray-500">Uploading File...</span>
              </div>
            }
            </div>
            {/* Main card */}
            <div className="w-full bg-white border border-[#E0E0E0] rounded-xl shadow-lg overflow-hidden">
              {/* Input area */}
              <div className="px-5 sm:px-[26px] pt-0">
                <TextInput
                  inputRef={inputRef}
                  inputText={prompt}
                  placeholder="Ask Dabur GPT"
                  handleChatResponse={handleChatMessage}
                  setInputText={setPrompt}
                  inputFocused={inputFocused}
                  setInputFocused={setInputFocused}
                  loading={loading}
                  disabled={loading && chats.length > 0}
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                  isUploading={isUploading}
                  files={files}
                  isMultipleFileUploadEnabled={true}
                  setFiles={setFiles}
                  chats={chats}
                  isFileUploadOptionVisible={true}
                  isContentRendering={loading}
                  requireMediaToSend={false}
                  showPromptActions={false}
                  showInlineUtilities={false}
                  useNativePlaceholder={true}
                  inputVariant="flat"
                  allowedFileTypes=".pptx,.ppt,.docx,.doc,.xlsx,.pdf,.txt,.md"
                />
              </div>

              {/* Toggles row */}
              <div className="bg-[#1B5C290D] px-5 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-6 flex-wrap">
                  <Toggle
                    label="Include Cover"
                    checked={includeCover}
                    onChange={() => setIncludeCover((v) => !v)}
                    tooltip="Welcome slide will also be generated"
                  />
                  <Toggle
                    label="Include Table of Contents"
                    checked={includeToc}
                    onChange={() => setIncludeToc((v) => !v)}
                    tooltip="Auto-generates a Table of Contents slide for easy navigation"
                  />
                  <Toggle
                    label="Include Images"
                    checked={includeImages}
                    onChange={() => setIncludeImages((v) => !v)}
                    tooltip="AI-generated images will be added to make the PPT more interactive"
                  />
                </div>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (!loading && !isUploading)
                        fileInputRef.current?.click();
                    }}
                    className="p-2 rounded-md hover:bg-white/60 transition"
                    aria-label="Upload file"
                  >
                    <Paperclip className="w-5 h-5 text-[#1B5C29]" />
                  </button>
                  <button
                    type="button"
                    onClick={toggleRecording}
                    className="p-2 rounded-md hover:bg-white/60 transition"
                    aria-label="Voice input"
                  >
                    <Mic
                      className={`w-5 h-5 ${isRecording ? "text-[#1B5C29]" : "text-[#1B5C29]"
                        }`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleChatMessage({ text: prompt, media: files })
                    }
                    className={classNames(
                      "p-2 rounded-md bg-[#1B5C29] shadow-sm",
                      {
                        "opacity-50 cursor-not-allowed": loading || isUploading,
                      }
                    )}
                    aria-label="Send message"
                    disabled={loading || isUploading}
                  >
                    <Send className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Controls row */}
            <div ref={controlsRef} className="w-full mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 justify-center">
                <div>
                  <p className="ml-2 my-1 text-xs flex font-semi-bold">Tone 
                     <ToolTipQuestionMark tooltip="Tone for slide" />
                     </p>
                  <DropdownPill
                    label={style}
                    open={openMenu === "style"}
                    onToggle={() => {
                      if (openMenu === "style") {
                        setOpenMenu(null);
                      } else {
                        setOpenMenu("style");
                      }
                    }}
                    onClose={() => setOpenMenu(null)}
                    options={Object.keys(TONE_OPTIONS)}   // 👈 KEY FIX
                    onSelect={(key) => {
                      setStyle(key);                      // display title
                      setTone(TONE_OPTIONS[key]);         // actual value (optional)
                      setOpenMenu(null);
                    }}
                  />
                </div>
                <div>
                  <p className="ml-2 my-1 text-xs flex font-semi-bold" >Number of slides
                  <ToolTipQuestionMark tooltip="Total slides" />
                  </p>
                  <DropdownPill
                    label={slideOption}
                    open={openMenu === "slides"}
                    onToggle={() => {
                      if (openMenu === "slides") {
                        setOpenMenu(null);
                      } else {
                        setOpenMenu("slides");
                      }
                    }}
                    onClose={() => setOpenMenu(null)}
                    options={SLIDE_LABEL_OPTIONS}
                    onSelect={(v) => {
                      setSlideOption(v);
                      setOpenMenu(null);
                    }}
                  />
                </div>

                <div>
                  <p className="ml-2 my-1 text-xs flex font-semi-bold">Level of detail
                    <ToolTipQuestionMark tooltip={"Depth of information"}/>
                  </p>
                  <DropdownPill
                    label={detailLevel}
                    open={openMenu === "detail"}
                    onToggle={() => {
                      if (openMenu === "detail") {
                        setOpenMenu(null);
                      } else {
                        setOpenMenu("detail");
                      }
                    }}
                    onClose={() => setOpenMenu(null)}
                    options={DETAIL_OPTIONS}
                    onSelect={(v) => {
                      setDetailLevel(v);
                      setOpenMenu(null);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="w-full max-w-5xl mx-auto mt-2">
            <p className="text-xs text-gray-500 text-center">
            {MESSAGES.POWERED_BY}
            </p>
          </div>
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <Modal
          open={pptPreview.open}
          onClose={() =>
            setPptPreview((prev) => ({
              ...prev,
              open: false,
            }))
          }
          slotProps={{
            backdrop: {
              sx: { backgroundColor: "rgba(0,0,0,0.3)" },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              borderRadius: "16px",
              boxShadow: 24,
              maxWidth: 1024,
              width: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="flex justify-end px-4 py-3 ">
              <button
                type="button"
                onClick={() =>
                  setPptPreview((prev) => ({ ...prev, open: false }))
                }
                className="text-[#6B7280] hover:text-[#111827] text-xl leading-none"
                aria-label="Close preview"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto px-8 py-6 ">
              {/* Main slide – just the currently active image */}
              <div className="flex justify-center mb-6">
                {pptPreview.slides?.length > 0 &&
                  pptPreview.slides[pptPreview.activeIndex] ? (
                  <img
                    src={getSlideSrc(pptPreview.slides[pptPreview.activeIndex])}
                    alt={`Slide ${pptPreview.activeIndex + 1}`}
                    loading="eager"
                    className="max-h-[420px]  rounded-[12px] border border-[rgba(27,92,41,0.12)] shadow-md object-contain bg-white"
                    onError={(e) => {
                      console.error(
                        "Failed to load slide image at index:",
                        pptPreview.activeIndex
                      );
                      e.currentTarget.src = "/images/slide-placeholder.png"; // optional fallback
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center max-h-[420px] w-[780px] rounded-[12px] border border-[rgba(27,92,41,0.12)] shadow-md bg-gray-100">
                    <span className="text-gray-500">No slides available</span>
                  </div>
                )}
              </div>

              {/* Thumbnail strip with Material UI-style carousel arrows */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
                sx={{ maxWidth: 800, mx: "auto" }}
              >
                <IconButton
                  size="small"
                  sx={{ width: 34, height: 34 }}
                  onClick={() => {
                    setPptPreview((prev) => {
                      const nextIndex =
                        prev.activeIndex > 0 ? prev.activeIndex - 1 : 0;
                      setTimeout(() => {
                        const el = document.getElementById(
                          `ppt-thumb-${nextIndex}`
                        );
                        el?.scrollIntoView({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }, 0);
                      return { ...prev, activeIndex: nextIndex };
                    });
                  }}
                  disabled={pptPreview.activeIndex === 0}
                >
                  <ChevronLeft className="w-5 h-5" />
                </IconButton>

                <div className="flex items-center justify-start gap-3 overflow-x-auto pb-2 max-w-[700px] w-full px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                  {pptPreview.slides?.length > 0
                    ? pptPreview.slides.map((s, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() =>
                          setPptPreview((prev) => ({
                            ...prev,
                            activeIndex: idx,
                          }))
                        }
                        className={`shrink-0 border rounded-[10px] overflow-hidden bg-white ${idx === pptPreview.activeIndex
                          ? "border-[#1B5C29] shadow-[0_0_0_2px_rgba(27,92,41,0.25)]"
                          : "border-[rgba(27,92,41,0.15)]"
                          }`}
                        id={`ppt-thumb-${idx}`}
                      >
                        <img
                          src={getSlideSrc(s)}
                          alt={`Slide ${idx + 1}`}
                          loading="lazy"
                          className="w-[180px] h-[110px] object-contain"
                          onError={(e) => {
                            console.error(
                              "Failed to load thumbnail at index:",
                              idx
                            );
                            e.currentTarget.src =
                              "/images/slide-placeholder.png"; // optional
                          }}
                        />
                      </button>
                    ))
                    : null}
                </div>

                <IconButton
                  size="small"
                  sx={{ width: 34, height: 34 }}
                  onClick={() => {
                    setPptPreview((prev) => {
                      const lastIndex = (prev.slides.length || 1) - 1;
                      const nextIndex =
                        prev.activeIndex < lastIndex
                          ? prev.activeIndex + 1
                          : prev.activeIndex;
                      setTimeout(() => {
                        const el = document.getElementById(
                          `ppt-thumb-${nextIndex}`
                        );
                        el?.scrollIntoView({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }, 0);
                      return { ...prev, activeIndex: nextIndex };
                    });
                  }}
                  disabled={
                    pptPreview.activeIndex >= pptPreview.slides.length - 1
                  }
                >
                  <ChevronRight className="w-5 h-5" />
                </IconButton>
              </Box>
            </div>
          </Box>
        </Modal>
      </div>
    );
  }

  if (view === "outline" && presentation) {
    return (
      <div className="flex-1 bg-gradient-to-b from-white to-[#E7EEE9] flex flex-col">
        {/* Scrollable content (offset for fixed top bar) */}
        <div className="flex-1 overflow-y-auto w-full px-4 sm:px-6 md:px-8 pt-20 pb-28">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-normal text-[#333333]">
                Presentation outline
              </h2>
              <div className="flex items-center gap-3">
                {/* <ThemeSelector
                  label="Title"
                  index={themeIndex}
                  onChange={setThemeIndex}
                /> */}
                {/* <Button
                  type="button"
                  onClick={() => setShowThemeModal(true)}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-[8px] bg-[#1B5C29] text-white text-sm font-semibold shadow-sm normal-case"
                >
                  <span className="translate-y-0.5">
                    Choose Theme
                  </span>
                </Button> */}
              </div>
            </div>

            <div className="mt-[8px] flex flex-wrap items-center gap-2">
             <span className="text-[12px] fonr-normal">
                {presentation?.slides?.length || 0}  Slides{" "}
              </span>
              <span className="text-[12px] fonr-normal">
                • {presentation?.meta?.tone}{" "}
              </span>
              <span className="text-[12px] fonr-normal">
                • {presentation?.meta?.detailLevel}{" "}
              </span>
            </div>

            <div className="mt-6 space-y-3">
              {presentation?.slides?.map((s, index) =>{
                const isOnlySlide = presentation.slides.length === 1;
               return s?.id === activeSlideId ? (
                  <ExpandedOutlineCard
                    key={s.id}
                    slide={s}
                    index={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    onCollapse={() => setActiveSlideId(null)}
                    onEdit={() => handleEditSlide(s.id)}
                    onDelete={() => handleDeleteSlide(s.id)}
                    isLast={isOnlySlide}
                  />
                ) : (
                  <SlideRow
                    key={s.id}
                    slide={s}
                    index={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    onSelect={() => handleSelectSlide(s.id)}
                    onEdit={() => handleEditSlide(s.id)}
                    onDelete={() => handleDeleteSlide(s.id)}
                  />
                )
              })}

              {/* <button
                type="button"
                onClick={handleAddSlide}
                className="w-full border border-dashed border-[#DEDEDE] bg-white/40 rounded-[13px] px-5 py-4 flex items-center gap-3 text-[#1B5C29]"
              >
                <Plus className="w-5 h-5" />
                <span className="text-base">Add Slide</span>
              </button> */}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
         <div className="sticky bottom-0 left-0 right-0 h-[100px] bg-white/80 backdrop-blur border-t border-[#E0E0E0] shadow-[14px_-6px_24px_0px_rgba(0,0,0,0.1)] ">
          <div className="max-w-6xl mx-auto px-4 h-[100px] sm:px-6 md:px-10 lg:px-12  flex items-center justify-center flex flex-col gap-2 justify-center items-center">
            {/* {isButtonDisabled &&
            } */}
            {showDisclaimer && initialUploadInProgress && (
              <h1 className="text-center text-sm text-gray-500">
                <i>
                  Please wait while we configure the presentation to align with
                  the outline...
                </i>
              </h1>
           )}
            <button
              type="button"
              onClick={handleGenerateFinalPresentation}
              disabled={isGeneratingFinal}
              className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-[#1B5C29] text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="translate-y-0.5">
                {isGeneratingFinal ? "Generating..." : "Generate Presentation"}
              </span>
            </button>
          </div>
        </div>
        <SiderModal
          open={showThemeModal}
          onClose={() => setShowThemeModal(false)}
          expanded={true}
          className="!rounded-tl-[12px] !rounded-bl-[12px] !rounded-tr-none !rounded-br-none"
          style={{ width: "70%" }}
        >
          <ThemeSelectionModal
            isOpen={showThemeModal}
            onClose={() => setShowThemeModal(false)}
            initialThemeIndex={themeIndex}
            initialTemplateId={activeTemplateId}
            onApply={handleThemeApply}
          />
        </SiderModal>
      </div>
    );
  }

  if (view === "edit" && presentation) {
    const activeSlide =
      presentation?.slides?.find((s) => s?.id === activeSlideId) ||
      presentation?.slides?.[0];

      const currentSlide = presentation?.slides?.filter((s) => s?.id !== activeSlideId);

    return (
      <div className="flex-1 bg-gradient-to-b from-white to-[#E7EEE9] flex flex-col">
        {/* Scrollable content (offset for fixed top bar) */}
        <div className="flex-1 overflow-y-auto w-full px-4 sm:px-6 md:px-8 pt-20 pb-28">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-normal text-[#333333]">
                Presentation outline
              </h2>
              <div className="flex items-center gap-3">
                {/* <ThemeSelector
                  label="Title"
                  index={themeIndex}
                  onChange={setThemeIndex}
                /> */}
                {/* <button
                  type="button"
                  onClick={() => setShowThemeModal(true)}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-[8px] bg-[#1B5C29] text-white text-sm font-semibold shadow-sm"
                >
                  <span className="translate-y-0.5">
                  Choose Theme
                  </span>
                </button> */}
              </div>
            </div>

            <div className="mt-[8px] flex flex-wrap items-center gap-2">
             <span className="text-[12px] fonr-normal">
                {presentation?.slides?.length || 0}  Slides{" "}
              </span>
              <span className="text-[12px] fonr-normal">
                • {presentation?.meta?.tone}{" "}
              </span>
              <span className="text-[12px] fonr-normal">
                • {presentation?.meta?.detailLevel}{" "}
              </span>
            </div>

            {/* Edit layout (matches Figma 1631:10793) */}
            <div className="mt-6 bg-white border border-[#E0E0E0] rounded-[13px] shadow-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <span
                    className="inline-flex
                items-center px-3 pt-1 rounded-full bg-[rgba(27,92,41,0.08)] border border-[#1B5C29] text-[#1B5C29] text-xs whitespace-nowrap"
                  >
                    {activeSlide?.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <input
                      value={
                        isEditingSlide ? editDraft?.title : activeSlide?.title
                      }
                      onChange={(e) =>
                        setEditDraft((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      readOnly={!isEditingSlide}
                      className="w-full border border-[#E0E0E0] rounded-[13px] px-4 py-2 text-sm text-[#1B5C29] focus:outline-none"
                      placeholder="Title"
                    />
                    <textarea
                      value={
                        isEditingSlide
                          ? editDraft?.description
                          : activeSlide?.description || ""
                      }
                      onChange={(e) =>
                        setEditDraft((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      readOnly={!isEditingSlide}
                      className="mt-3 w-full border border-[#E0E0E0] rounded-[13px] px-4 py-3 text-sm text-[#1B5C29] focus:outline-none resize-none"
                      rows={4}
                      placeholder="Description"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isEditingSlide ? (
                    <>
                      <IconBtn
                        label="Edit"
                        onClick={() => {
                          setIsEditingSlide(true);
                          setEditDraft({
                            title: activeSlide?.title || "",
                            description: activeSlide?.description || "",
                          });
                        }}
                      >
                        <SquarePen className="w-4 h-4 text-[#1B5C29]" />
                      </IconBtn>
                      <IconBtn
                        label="Delete"
                        onClick={() => handleDeleteSlide(activeSlide?.id)}
                      >
                        <Trash2 className="w-4 h-4 text-[#1B5C29]" />
                      </IconBtn>
                      <IconBtn label="Move" onClick={() => { }}>
                        <Move className="w-4 h-4 text-[#1B5C29]" />
                      </IconBtn>
                    </>
                  ) : (
                    <>
                      <IconBtn
                        label="Save"
                        variant="check"
                        onClick={() => {
                          handleUpdateSlide(activeSlide?.id, {
                            title: editDraft?.title,
                            description: editDraft?.description,
                          });
                          setIsEditingSlide(false);
                          setView("outline");
                        }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </IconBtn>
                      <IconBtn
                        label="Cancel"
                        variant="cross"
                        onClick={() => {
                          setIsEditingSlide(false);
                          setEditDraft({
                            title: activeSlide?.title || "",
                            description: activeSlide?.description || "",
                          });
                          setView("outline");
                        }}
                      >
                        <X className="w-4 h-4 text-[#F4711F]" />
                      </IconBtn>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {currentSlide?.map((s, index) => (
                <SlideRow
                  key={s.id}
                  slide={s}
                  index={index}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  onSelect={() => handleSelectSlide(s.id)}
                  onEdit={() => handleEditSlide(s.id)}
                  onDelete={() => handleDeleteSlide(s.id)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
         <div className="sticky bottom-0 left-0 right-0 h-[100px] bg-white/80 backdrop-blur border-t border-[#E0E0E0] shadow-[14px_-6px_24px_0px_rgba(0,0,0,0.1)] ">
          <div className="max-w-6xl mx-auto px-4 h-[100px] sm:px-6 md:px-10 lg:px-12  flex items-center justify-center flex flex-col gap-2 justify-center items-center">
            {/* {isButtonDisabled &&
            } */}
            {showDisclaimer && initialUploadInProgress && (
              <h1 className="text-center text-sm text-gray-500">
                <i>
                  Please wait while we configure the presentation to align with
                  the outline...
                </i>
              </h1>
           )}
            <button
              type="button"
              onClick={handleGenerateFinalPresentation}
              disabled={isGeneratingFinal}
              className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-[#1B5C29] text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="translate-y-0.5">
                {isGeneratingFinal ? "Generating..." : "Generate Presentation"}
              </span>
            </button>
          </div>
        </div>
        <SiderModal
          open={showThemeModal}
          onClose={() => setShowThemeModal(false)}
          expanded={true}
          className="!rounded-tl-[12px] !rounded-bl-[12px] !rounded-tr-none !rounded-br-none"
          style={{ width: "65%" }}
        >
          <ThemeSelectionModal
            isOpen={showThemeModal}
            onClose={() => setShowThemeModal(false)}
            initialThemeIndex={themeIndex}
            initialTemplateId={activeTemplateId}
            onApply={handleThemeApply}
          />
        </SiderModal>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gradient-to-b from-white to-[#E7EEE9] overflow-y-auto">
      <div className="min-h-full sm:h-full sm:min-h-[calc(100vh-3rem)] flex flex-col items-center justify-start sm:justify-center w-full px-4 sm:px-6 md:px-10 lg:px-12 pt-20 sm:pt-10 pb-10">
        <div className="w-full flex flex-col items-center text-center">
          <h1 className="text-3xl sm:text-4xl md:text-[42px] leading-tight text-[#333333] font-normal">
            Create Professional Presentations in Minutes
          </h1>
          <p className="mt-3 text-base sm:text-lg md:text-[22px] leading-snug text-[#F4711F] max-w-3xl">
            Turn ideas, documents, or data into polished, on-brand
            slides—automatically.
          </p>
        </div>

        {/* File preview cards - outside the input box */}
        <div className="w-full md:max-w-[768px] 2xl:max-w-5xl mt-4">
         <div className="flex flex-row  gap-4 items-center">
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {files.map((file, index) => (
                  <UploadFileCard
                    key={`uploadfilecard-${file?.fileUrl}`}
                    file={file}
                    index={index}
                    removeFile={removeFile}
                  />
                ))}
              </div>
            )}
            {isUploading  && <div className="flex flex-row gap-4 items-center ">
                <UploadingLoader />
                <span className="text-sm text-gray-500">Uploading File...</span>
              </div>
            }
          </div>
        </div>
        {/* Main card */}
        <div
          className={`w-full md:max-w-[768px] 2xl:max-w-5xl ${files.length > 0 ? "mt-2" : "mt-2"
            } bg-white border border-[#E0E0E0] rounded-xl shadow-lg overflow-hidden`}
        >
          {/* Input area */}
          <div className="px-5 sm:px-[26px] pt-0">
            <TextInput
              inputRef={inputRef}
              inputText={prompt}
              placeholder="Ask Dabur GPT"
              handleChatResponse={handleGenerate}
              setInputText={setPrompt}
              inputFocused={inputFocused}
              setInputFocused={setInputFocused}
              loading={loading}
              disabled={false}
              fileInputRef={fileInputRef}
              handleFileChange={handleFileChange}
              isUploading={isUploading}
              files={files}
              isMultipleFileUploadEnabled={true}
              setFiles={setFiles}
              chats={chats}
              isFileUploadOptionVisible={true}
              isContentRendering={loading}
              requireMediaToSend={false}
              showPromptActions={false}
              showInlineUtilities={false}
              useNativePlaceholder={true}
              inputVariant="flat"
              allowedFileTypes=".pptx,.ppt,.docx,.doc,.xlsx,.pdf,.txt,.md"
            />
          </div>

          {/* Toggles row */}
          <div className="bg-[#1B5C290D] px-5 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-6 flex-wrap">
              <Toggle
                label="Include Cover"
                checked={includeCover}
                onChange={() => setIncludeCover((v) => !v)}
                tooltip="Welcome slide will also be generated"
              />
              <Toggle
                label="Include Table of Contents"
                checked={includeToc}
                onChange={() => setIncludeToc((v) => !v)}
                tooltip="Auto-generates a Table of Contents slide for easy navigation"
              />
              <Toggle
                label="Include Images"
                checked={includeImages}
                onChange={() => setIncludeImages((v) => !v)}
                tooltip="AI-generated images will be added to make the PPT more interactive"
              />
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  if (!loading && !isUploading) fileInputRef?.current?.click();
                }}
                className="p-2 rounded-md hover:bg-white/60 transition"
                aria-label="Upload file"
              >
                <Paperclip className="w-5 h-5 text-[#1B5C29]" />
              </button>
              <button
                type="button"
                onClick={toggleRecording}
                className="p-2 rounded-md hover:bg-white/60 transition"
                aria-label="Voice input"
              >
                <Mic
                  className={`w-5 h-5 ${isRecording ? "text-[#1B5C29]" : "text-[#1B5C29]"
                    }`}
                />
              </button>
              {/* <button
              type="button"
              className="p-2 rounded-md hover:bg-white/60 transition"
              aria-label="Voice mode"
              disabled
            >
              <AudioLines className="w-5 h-5 text-[#1B5C29]" />
            </button> */}
              <button
                type="button"
                onClick={() => handleGenerate({ text: prompt, media: files })}
                className={classNames("p-2 rounded-md bg-[#1B5C29] shadow-sm", {
                  "opacity-50 cursor-not-allowed": loading || isUploading,
                })}
                aria-label="Generate presentation"
                disabled={loading || isUploading}
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Controls row */}
        <div
          ref={controlsRef}
          className="w-full md:max-w-[768px] 2xl:max-w-5xl mt-[21px]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 justify-center">
            <div>
             <p className="ml-2 my-1 text-xs flex font-semi-bold" >Tone
                <ToolTipQuestionMark tooltip="Tone of slide" />
              </p>
            <DropdownPill
              label={style}
              open={openMenu === "style"}
              onToggle={() => {
                if (openMenu === "style") {
                  setOpenMenu(null);
                } else {
                  setOpenMenu("style");
                }
              }}
              onClose={() => setOpenMenu(null)}
              options={Object.keys(TONE_OPTIONS)}   // 👈 KEY FIX
              onSelect={(key) => {
                setStyle(key);                      // display title
                setTone(TONE_OPTIONS[key]);         // actual value (optional)
                setOpenMenu(null);
              }}
            />
            </div>

            <div>
                  <p className="ml-2 my-1 text-xs flex font-semi-bold" >Number of slides
                  <ToolTipQuestionMark tooltip="Total slides" />
                  </p>

            <DropdownPill
              label={slideOption}
              open={openMenu === "slides"}
              onToggle={() => {
                if (openMenu === "slides") {
                  setOpenMenu(null);
                } else {
                  setOpenMenu("slides");
                }
              }}
              onClose={() => setOpenMenu(null)}
              options={SLIDE_LABEL_OPTIONS}
              onSelect={(v) => {
                setSlideOption(v);
                setOpenMenu(null);
              }}
            />
            </div>
            <div>
              <p className="ml-2 my-1 text-xs flex font-semi-bold">Level of detail
                <ToolTipQuestionMark tooltip={"Depth of information"}/>
              </p>
            <DropdownPill
              label={detailLevel}
              open={openMenu === "detail"}
              onToggle={() => {
                if (openMenu === "detail") {
                  setOpenMenu(null);
                } else {
                  setOpenMenu("detail");
                }
              }}
              onClose={() => setOpenMenu(null)}
              options={DETAIL_OPTIONS}
              onSelect={(v) => {
                setDetailLevel(v);
                setOpenMenu(null);
              }}
            />
            </div>
          </div>
        </div>

        <p className="mt-8 text-xs text-[#9CA3AF]"> {MESSAGES.POWERED_BY}
 </p>
      </div>
    </div>
  );
}

export function Toggle({ label, checked, onChange, tooltip }) {
  const ref = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={onChange}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="inline-flex items-center gap-2 text-sm text-[#1B5C29]"
      >
        <span
          className={`w-8 h-5 flex items-center rounded-full px-[2px] transition-colors ${checked ? "bg-[#1B5C29]" : "bg-gray-300"
            }`}
        >
          <span
            className={`w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0"
              }`}
          />
        </span>

        <span className="font-normal pt-1 text-[12px] text-[#1B5C29]">
          {label}
        </span>
      </button>

      {tooltip && (
        <Tooltip
          anchorRef={ref}
          text={tooltip}
          visible={showTooltip}
        />
      )}
    </>
  );
}


function DropdownPill({ label, open, onToggle, onClose, options, onSelect }) {
  const anchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, [anchorRef]);

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="w-full">
      <button
        ref={anchorRef}
        type="button"
        onClick={onToggle}
        className="w-full inline-flex items-center justify-between px-4 py-2.5 text-sm rounded-md border border-[#E0E0E0] bg-white text-[#1B5C29] hover:bg-[#FAFAFA] focus:outline-none"
      >
        <span className="truncate pt-0.3">{label}</span>
        <ChevronDown size={16} className="text-[#1B5C29]" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open && Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: anchorRef.current?.offsetWidth || "auto",
              maxHeight: "170px",
              border: "1px solid #E0E0E0",
              borderRadius: "6px",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
              overflowY: "auto",
              scrollbarColor: "#F4711F #F4711F33",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F4711F33",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#F4711F",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#F4711F",
                },
              },
            },
          },
        }}
      >
        {options?.map((opt) => (
          <MenuItem
            key={opt}
            onClick={() => {
              onSelect(opt);
              onClose();
            }}
            selected={opt === label}
            sx={{
              fontSize: "14px",
              color: "#1B5C29",
              "&.Mui-selected": {
                backgroundColor: "#E7EEE9",
                "&:hover": {
                  backgroundColor: "#E7EEE9",
                },
              },
              "&:hover": {
                backgroundColor: "#E7EEE9",
              },
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  children,
  variant = "ghost",
  className = "",
}) {
  const base =
    "w-[38px] h-[34px] rounded-full flex items-center justify-center transition";
  let palette = "bg-[rgba(27,92,41,0.1)] hover:bg-[rgba(27,92,41,0.14)]";

  if (variant === "solid") {
    palette = "bg-[#1B5C29] hover:bg-[#154421]";
  } else if (variant === "check") {
    // Check button styling (dark green)
    palette = "bg-[#1B5C29] hover:bg-[#154421]";
  } else if (variant === "cross") {
    // Cross button styling (light orange background)
    palette = "bg-[#FFE6D7] hover:bg-[#ffd1b5]";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${palette} ${className}`}
      aria-label={label}
    >
      {children}
    </button>
  );
}

function SlideRow({
  slide,
  onSelect,
  onEdit,
  onDelete,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
}) {
  return (
    <div
      className="w-full bg-white border border-[#E0E0E0] rounded-[13px] px-5 py-4 flex items-center justify-between gap-4 cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={onSelect}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect?.();
      }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <span className="inline-flex items-center px-3 pb-1 pt-1.5 rounded-full bg-[rgba(27,92,41,0.08)] border border-[#1B5C29] text-[#1B5C29] text-xs">
          {slide?.label}
        </span>
        <span className="text-[14px] font-bold text-[#1B5C29] truncate">
          {slide?.title}
        </span>
      </div>
    </div>
  );
}

function ExpandedOutlineCard({
  slide,
  onCollapse,
  onEdit,
  onDelete,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  isLast
}) {
  return (
    <div
      className="w-full bg-white border border-[#E0E0E0] rounded-[13px] shadow-sm px-5 py-5 flex items-start justify-between gap-4"
      role="button"
      tabIndex={0}
      onClick={onCollapse}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onCollapse?.();
      }}
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center px-3 pb-1 pt-1.5 rounded-full bg-[rgba(27,92,41,0.08)] border border-[#1B5C29] text-[#1B5C29] text-xs whitespace-nowrap">
          {slide?.label}
        </span>
        <div>
          <h3 className="text-sm font-bold text-[#1B5C29]">{slide?.title}</h3>
          {slide?.label === "Cover Slide" && (
            <ul className="mt-2 font-normal text-[14px]  text-[#1B5C29] max-w-3xl list-disc pl-5 space-y-2">
              <li>
                A dynamic cover slide will be created based on your content.
              </li>
              <li>
                You can edit the title and choose a different layout to
                customize it as you need.
              </li>
              <li>
                If you choose layout different from Cover layouts, the outline
                will automatically be created again
              </li>
            </ul>
          )}
          {slide?.label !== "Cover Slide" && (
            <p className="mt-2 font-normal text-[14px] text-[#1B5C29] max-w-3xl">
              {slide?.description?.trim()
                ? slide?.description
                : "This slide is part of your outline."}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <IconBtn
          label="Edit"
          onClick={(e) => {
            e?.stopPropagation?.();
            onEdit?.();
          }}
        >
          <SquarePen className="w-4 h-4 text-[#1B5C29]" />
        </IconBtn>
        {!isLast && (
          <IconBtn
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <Trash2 className="w-4 h-4 text-[#1B5C29]" />
          </IconBtn>
        )}
        {/* <IconBtn
          label="Move"
          className="cursor-grab active:cursor-grabbing"
          onClick={(e) => {
            e?.stopPropagation?.();
          }}
        >
          <Move className="w-4 h-4 text-[#1B5C29]" />
        </IconBtn> */}
      </div>
    </div>
  );
}
